import { Controller, useForm, useWatch } from "react-hook-form";
import { ja } from "date-fns/locale/ja";
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

import { ExternalServiceCodeType } from "types/externalService";

import { dayMap } from "./helper";
import { MapOption, ScheduleFormInput } from "./type";
import { getCleaningSettingFields } from "utils/robot/cleaningSetting/cleaningSetting";

type ScheduleFormProps = {
  exSrvCode: ExternalServiceCodeType;
  maps: MapOption[];
  control: ReturnType<typeof useForm<ScheduleFormInput>>["control"];
  errors: ReturnType<typeof useForm<ScheduleFormInput>>["formState"]["errors"];
};

export const ScheduleForm = ({
  exSrvCode,
  maps,
  control,
  errors,
}: ScheduleFormProps) => {
  const selectedCleanType = useWatch({
    control,
    name: "cleaningSettings.cleanType",
  });

  const cleaningSettingRules = getCleaningSettingFields(exSrvCode, {
    cleanType: selectedCleanType,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Stack spacing={2}>
        <Controller
          name="scheduleName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="スケジュール名"
              fullWidth
              error={!!errors.scheduleName}
              helperText={errors.scheduleName?.message}
            />
          )}
        />
        <Controller
          name="dayOfWeek"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel>曜日</InputLabel>
              <Select {...field} label="曜日" error={!!errors.dayOfWeek}>
                {Object.entries(dayMap).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {errors.dayOfWeek && (
                <Typography variant="caption" color={"error"}>
                  {errors.dayOfWeek.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="time"
          control={control}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label="開始時刻"
              ampm={false}
              ampmInClock={false}
              desktopModeMediaQuery="@media all"
              slotProps={{
                textField: {
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />

        <Controller
          name="mapId"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel>マップ</InputLabel>
              <Select {...field} label="マップ" error={!!errors.mapId}>
                {maps.map(({ mapId, mapName }) => (
                  <MenuItem key={mapId} value={mapId}>
                    {mapName}
                  </MenuItem>
                ))}
              </Select>
              {errors.mapId && (
                <Typography variant="caption" color={"error"}>
                  {errors.mapId.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
        <Box>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <CleaningServicesIcon
              sx={{ color: "text.secondary", fontSize: "20px" }}
            />
            <Typography variant="subtitle1" color={"text.seconday"}>
              清掃設定
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            {cleaningSettingRules.map((field) => {
              const type = field.type;
              const typeLabel = field.typeLabel;
              const options = field.options;
              if (field.hide) return null;
              return (
                <Controller
                  key={type}
                  name={`cleaningSettings.${type}`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>{typeLabel}</InputLabel>
                      <Select {...field} label={typeLabel}>
                        {options.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              );
            })}
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Controller
          name="memo"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="備考" multiline rows={3} fullWidth />
          )}
        />
      </Stack>
    </LocalizationProvider>
  );
};
